// ─── meteoraTank: the DLMM bid-side fuel gauge, one owner ───
//
// FPCC owns useMeteoraBinLiquidity (it draws the bins). The Tracker panel is
// a SIBLING of the chart and can't reach that hook — and calling it again
// there would mean two RPC pollers for one fact, the same two-stores bug
// that made wallet stats and the trail's follow list read empty boxes. So
// FPCC publishes here on every bin update, and any panel reads by mint.
//
// ─── What the gauge measures ───
// In a DLMM, bins BELOW the active bin hold SOL — that is the pool's bid,
// the capacity to absorb selling. Bins above hold token. Sells eat the SOL
// bins bottom-up, so:
//
//   tank      Σ solReserve over bid bins — gallons in the tank right now
//   peak      the largest tank seen this session — TANK SIZE. Without it,
//             "40 SOL" is unreadable: is that full or nearly dry?
//   ratio     tank / mcap — STRUCTURAL size. 40 SOL under a 20k mcap is a
//             reservoir; the same 40 under 2M is a thimble. Absolute SOL
//             does not compare across coins; this does.
//   drain     SOL per minute, from a least-squares fit over the sample
//             window. A single first-vs-last delta reads whatever noise the
//             endpoints happen to carry; the fit uses every sample.
//   etaMin    tank / drain — MINUTES TO EMPTY at the current rate. This is
//             the number that changes behaviour: 30% full and static is a
//             different market from 30% full draining at 2 SOL/min.
//   floorPct  how far price can fall before the bid bins are exhausted.
//             Concrete downside: "12% down and there is no pool bid left."
//
// ─── The honesty flag ───
// The hook fetches a fixed number of bins each side. If the lowest bid bin
// still holds SOL, the window is a SLICE and the true tank is larger than
// measured. `partial` says so rather than quietly under-reporting — a gauge
// that lies low is worse than no gauge.

const TANKS = new Map()            // mint -> { samples, peak, partial, floorPct, mcap }
const SAMPLE_WINDOW_MS = 10 * 60_000
const MAX_MINTS = 8

/**
 * Server-fed tank sample. The DLMM bins came from an in-browser SDK that
 * cost ~86 RPC calls per token load, so that path is off; the server polls
 * Meteora's REST API (which the browser can't call — CORS) and broadcasts
 * `meteora_tank`. Same registry, same derivations — only the source moved.
 *
 * No per-bin data here, so `floorPct` stays 0 and the UI hides it. Tank,
 * capacity, ratio, drain and ETA all work from the single reserve number.
 */
export function publishTankSample(mint, solReserve, mcap, sym, usd, extra) {
  if (!mint || !(solReserve >= 0)) return
  let T = TANKS.get(mint)
  if (!T) {
    T = { samples: [], peak: 0, partial: false, floorPct: 0, mcap: 0 }
    TANKS.set(mint, T)
    while (TANKS.size > MAX_MINTS) TANKS.delete(TANKS.keys().next().value)
  }
  const now = Date.now()
  T.samples.push({ t: now, tank: solReserve })
  const cut = now - SAMPLE_WINDOW_MS
  while (T.samples.length && T.samples[0].t < cut) T.samples.shift()
  if (solReserve > T.peak) T.peak = solReserve
  if (mcap > 0) T.mcap = mcap
  // Pools quote in SOL, USDC or USDT — the label travels with the number
  // rather than being assumed. usd gives a cross-coin scale for the ratio.
  if (sym) T.sym = sym
  if (usd > 0) T.usd = usd
  // Both-sides depth (server build 2026-08-19+). `extra` is the raw
  // meteora_tank payload; older servers omit these fields, so absence
  // means "bid-only feed" and the UI hides the tug bar rather than
  // faking a 100/0 split. bidUsd is kept even though it duplicates
  // `usd` today — the widget reads one namespace, not two.
  if (extra && typeof extra === 'object') {
    const num = (v) => (Number.isFinite(+v) && +v >= 0 ? +v : null)
    const b = num(extra.bidUsd), a = num(extra.askUsd)
    if (b != null) T.bidUsd = b
    if (a != null) T.askUsd = a
    const t = num(extra.totalUsd)
    T.totalUsd = t != null ? t : (b != null && a != null ? b + a : T.totalUsd)
  }
}

export function publishTank(mint, bins, mcap) {
  if (!mint) return
  let T = TANKS.get(mint)
  if (!T) {
    T = { samples: [], peak: 0, partial: false, floorPct: 0, mcap: 0 }
    TANKS.set(mint, T)
    while (TANKS.size > MAX_MINTS) TANKS.delete(TANKS.keys().next().value)
  }
  if (!Array.isArray(bins) || bins.length === 0) return

  let tank = 0, lowestBidMcap = Infinity, lowestBidSol = 0, activeMcap = 0
  for (const b of bins) {
    if (b.side === 'bid') {
      tank += b.solReserve
      if (b.mcap < lowestBidMcap) { lowestBidMcap = b.mcap; lowestBidSol = b.solReserve }
    } else if (b.side === 'active') {
      activeMcap = b.mcap
      tank += b.solReserve          // the active bin holds both; its SOL is still bid-side
    }
  }
  if (!activeMcap) activeMcap = mcap || 0

  const now = Date.now()
  T.samples.push({ t: now, tank })
  const cut = now - SAMPLE_WINDOW_MS
  while (T.samples.length && T.samples[0].t < cut) T.samples.shift()
  if (tank > T.peak) T.peak = tank
  // Lowest fetched bid bin still has SOL ⇒ liquidity continues past the
  // window we can see.
  T.partial = lowestBidSol > 0
  T.floorPct = (activeMcap > 0 && lowestBidMcap < Infinity)
    ? Math.max(0, (activeMcap - lowestBidMcap) / activeMcap)
    : 0
  T.mcap = activeMcap
}

export function getTank(mint) {
  const T = mint && TANKS.get(mint)
  if (!T || T.samples.length === 0) return null
  const tank = T.samples[T.samples.length - 1].tank

  // Least-squares slope over the window → SOL per minute. Every sample
  // votes, so one noisy endpoint can't invent a drain that isn't there.
  let drainPerMin = 0
  if (T.samples.length >= 3) {
    const t0 = T.samples[0].t
    let n = 0, sx = 0, sy = 0, sxy = 0, sxx = 0
    for (const s of T.samples) {
      const x = (s.t - t0) / 60000
      n++; sx += x; sy += s.tank; sxy += x * s.tank; sxx += x * x
    }
    const denom = n * sxx - sx * sx
    if (denom > 1e-9) drainPerMin = -((n * sxy - sx * sy) / denom)   // positive = draining
  }
  const etaMin = drainPerMin > 0.001 ? tank / drainPerMin : null

  return {
    tank,
    peak: T.peak,
    pctFull: T.peak > 0 ? tank / T.peak : 0,
    ratio: T.mcap > 0 ? tank / T.mcap : 0,     // SOL per $ of mcap
    drainPerMin,
    etaMin,
    floorPct: T.floorPct,
    partial: T.partial,
    sym: T.sym || '◎',
    usd: T.usd || 0,
    // null (not 0) when the feed is bid-only — the tug bar keys off this.
    bidUsd: T.bidUsd ?? null,
    askUsd: T.askUsd ?? null,
    totalUsd: T.totalUsd ?? null,
    samples: T.samples.length,
  }
}